import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { type User, type UserRole, type UserStatus, type UserFilters, DEFAULT_PERMISSIONS, ROLE_LABELS, ROLE_DESCRIPTIONS, type UserPermissions } from '@/types/user';
import { UsersTable } from '@/components/users/users-table';
import { ViewUserDialog } from '@/components/users/view-user-dialog';
import { DeleteUserDialog } from '@/components/users/delete-user-dialog';
import { ManageUserIntegrationsDialog } from '@/components/users/manage-user-integrations-dialog';
import { ManageUserCompaniesDialog } from '@/components/users/manage-user-companies-dialog';
import { useUsersList, useDeleteUser, useUpdateUser } from '@/hooks/useUsersData';
import { useApi } from '@/hooks/useApi';
import { Search, Plus, Edit, Trash2, Users as UsersIcon, Shield, Check, X, Download, Filter, Calendar as CalendarIcon, User as UserIcon, Activity, Eye, ChevronRight, Copy, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

// Activity Log Types
type ActivityType = 
  | 'user_login'
  | 'user_logout'
  | 'user_created'
  | 'user_updated'
  | 'user_deleted'
  | 'role_changed'
  | 'permission_changed'
  | 'settings_updated'
  | 'data_exported'
  | 'data_imported'
  | 'record_created'
  | 'record_updated'
  | 'record_deleted'
  | 'record_viewed';

interface ActivityLog {
  id: string;
  timestamp: string;
  userId: string;
  userName: string;
  userEmail: string;
  action: ActivityType;
  description: string;
  ipAddress: string;
  userAgent: string;
  resource?: string;
  resourceId?: string;
  changes?: Record<string, { old: string; new: string }>;
}

// Custom Role Interface
interface CustomRole {
  id: string;
  name: string;
  description: string;
  permissions: UserPermissions;
  userCount: number;
  isSystem: boolean;
  createdAt: string;
}

// System Roles - Initial data with placeholder counts (will be updated dynamically)
const SYSTEM_ROLES: CustomRole[] = [
  {
    id: 'administrator',
    name: 'Administrator',
    description: ROLE_DESCRIPTIONS.administrator,
    permissions: DEFAULT_PERMISSIONS.administrator,
    userCount: 2,
    isSystem: true,
    createdAt: '2026-02-05T00:00:00Z',
  },
  {
    id: 'user',
    name: 'User',
    description: ROLE_DESCRIPTIONS.user,
    permissions: DEFAULT_PERMISSIONS.user,
    userCount: 15,
    isSystem: true,
    createdAt: '2026-02-05T00:00:00Z',
  },
  {
    id: 'view_only',
    name: 'View Only',
    description: ROLE_DESCRIPTIONS.view_only,
    permissions: DEFAULT_PERMISSIONS.view_only,
    userCount: 8,
    isSystem: true,
    createdAt: '2026-02-05T00:00:00Z',
  },
  {
    id: 'collections_manager',
    name: 'Collections Manager',
    description: 'Custom role for managing collections and customer communications',
    permissions: {
      manageUsers: false,
      manageCompanies: false,
      manageIntegrations: false,
      configureAutomation: false,
      configureAI: false,
      viewReports: true,
      manageAccounts: true,
      sendCommunications: true,
      logActivities: true,
      viewPayments: true,
      allocatePayments: true,
      reverseAllocations: false,
      customerAccountsView: 'assigned_default',
      customerAccountsEdit: 'assigned',
      customerAccountsReassign: false,
      collectionCasesView: 'assigned_default',
      collectionCasesCreate: true,
      collectionCasesEdit: 'assigned',
      collectionCasesDelete: false,
      collectionCasesReassign: false,
      promiseToPayView: 'assigned_default',
      promiseToPayCreate: true,
      promiseToPayEdit: 'assigned',
      promiseToPayDelete: false,
      promiseToPayReassign: false,
      viewCreditControlDashboard: false,
      viewCollectionsDashboard: false,
      viewDisputesDashboard: false,
      viewAgeingAnalysis: false,
      viewMultiCompanyDashboard: false,
      accessManagerViews: false,
      adminManageUsersView: false,
      adminManageUsersCreate: false,
      adminManageUsersEdit: false,
      adminManageUsersDelete: false,
      adminManageCompaniesView: false,
      adminManageCompaniesCreate: false,
      adminManageCompaniesEdit: false,
      adminManageCompaniesDelete: false,
      adminManageGeneralSettingsView: false,
      adminManageGeneralSettingsCreate: false,
      adminManageGeneralSettingsEdit: false,
      adminManageGeneralSettingsDelete: false,
      adminManageIntegrationsView: false,
      adminManageIntegrationsCreate: false,
      adminManageIntegrationsEdit: false,
      adminManageIntegrationsDelete: false,
      adminManageTemplatesView: false,
      adminManageTemplatesCreate: false,
      adminManageTemplatesEdit: false,
      adminManageTemplatesDelete: false,
      adminManageCollectionStrategiesView: false,
      adminManageCollectionStrategiesCreate: false,
      adminManageCollectionStrategiesEdit: false,
      adminManageCollectionStrategiesDelete: false,
      adminManageDisputeTypesView: false,
      adminManageDisputeTypesCreate: false,
      adminManageDisputeTypesEdit: false,
      adminManageDisputeTypesDelete: false,
      adminConfigureAIView: false,
      adminConfigureAICreate: false,
      adminConfigureAIEdit: false,
      adminConfigureAIDelete: false,
    },
    userCount: 5,
    isSystem: false,
    createdAt: '2026-01-15T00:00:00Z',
  },
];

// Permission Labels and Descriptions
const PERMISSION_LABELS: Record<keyof UserPermissions, string> = {
  manageUsers: 'Manage Users',
  manageCompanies: 'Manage Companies',
  manageIntegrations: 'Manage Integrations',
  configureAutomation: 'Configure Automation',
  configureAI: 'Configure AI',
  viewReports: 'View Reports',
  manageAccounts: 'Manage Accounts',
  sendCommunications: 'Send Communications',
  logActivities: 'Log Activities',
  viewPayments: 'View Payments',
  allocatePayments: 'Allocate Payments',
  reverseAllocations: 'Reverse Allocations',
  customerAccountsView: 'Customer Accounts View',
  customerAccountsEdit: 'Customer Accounts Edit',
  customerAccountsReassign: 'Customer Accounts Reassign',
  collectionCasesView: 'Collection Cases View',
  collectionCasesCreate: 'Collection Cases Create',
  collectionCasesEdit: 'Collection Cases Edit',
  collectionCasesDelete: 'Collection Cases Delete',
  collectionCasesReassign: 'Collection Cases Reassign',
  promiseToPayView: 'Promise to Pay View',
  promiseToPayCreate: 'Promise to Pay Create',
  promiseToPayEdit: 'Promise to Pay Edit',
  promiseToPayDelete: 'Promise to Pay Delete',
  promiseToPayReassign: 'Promise to Pay Reassign',
  viewCreditControlDashboard: 'View Credit Control Dashboard',
  viewCollectionsDashboard: 'View Collections Dashboard',
  viewDisputesDashboard: 'View Disputes Dashboard',
  viewAgeingAnalysis: 'View Ageing Analysis',
  viewMultiCompanyDashboard: 'View Multi-Company Dashboard',
  accessManagerViews: 'Access Manager Views',
  adminManageUsersView: 'Admin: Manage Users - View',
  adminManageUsersCreate: 'Admin: Manage Users - Create',
  adminManageUsersEdit: 'Admin: Manage Users - Edit',
  adminManageUsersDelete: 'Admin: Manage Users - Delete',
  adminManageCompaniesView: 'Admin: Manage Companies - View',
  adminManageCompaniesCreate: 'Admin: Manage Companies - Create',
  adminManageCompaniesEdit: 'Admin: Manage Companies - Edit',
  adminManageCompaniesDelete: 'Admin: Manage Companies - Delete',
  adminManageGeneralSettingsView: 'Admin: Manage General Settings - View',
  adminManageGeneralSettingsCreate: 'Admin: Manage General Settings - Create',
  adminManageGeneralSettingsEdit: 'Admin: Manage General Settings - Edit',
  adminManageGeneralSettingsDelete: 'Admin: Manage General Settings - Delete',
  adminManageIntegrationsView: 'Admin: Manage Integrations - View',
  adminManageIntegrationsCreate: 'Admin: Manage Integrations - Create',
  adminManageIntegrationsEdit: 'Admin: Manage Integrations - Edit',
  adminManageIntegrationsDelete: 'Admin: Manage Integrations - Delete',
  adminManageTemplatesView: 'Admin: Manage Templates - View',
  adminManageTemplatesCreate: 'Admin: Manage Templates - Create',
  adminManageTemplatesEdit: 'Admin: Manage Templates - Edit',
  adminManageTemplatesDelete: 'Admin: Manage Templates - Delete',
  adminManageCollectionStrategiesView: 'Admin: Manage Collection Strategies - View',
  adminManageCollectionStrategiesCreate: 'Admin: Manage Collection Strategies - Create',
  adminManageCollectionStrategiesEdit: 'Admin: Manage Collection Strategies - Edit',
  adminManageCollectionStrategiesDelete: 'Admin: Manage Collection Strategies - Delete',
  adminManageDisputeTypesView: 'Admin: Manage Dispute Types - View',
  adminManageDisputeTypesCreate: 'Admin: Manage Dispute Types - Create',
  adminManageDisputeTypesEdit: 'Admin: Manage Dispute Types - Edit',
  adminManageDisputeTypesDelete: 'Admin: Manage Dispute Types - Delete',
  adminConfigureAIView: 'Admin: Configure AI - View',
  adminConfigureAICreate: 'Admin: Configure AI - Create',
  adminConfigureAIEdit: 'Admin: Configure AI - Edit',
  adminConfigureAIDelete: 'Admin: Configure AI - Delete',
};

const PERMISSION_DESCRIPTIONS: Record<keyof UserPermissions, string> = {
  manageUsers: 'Create, edit, and delete user accounts',
  manageCompanies: 'Manage company settings and configurations',
  manageIntegrations: 'Configure and manage system integrations',
  configureAutomation: 'Set up and modify automation rules',
  configureAI: 'Configure AI and machine learning settings',
  viewReports: 'Access and view system reports',
  manageAccounts: 'Manage customer accounts and data',
  sendCommunications: 'Send emails and communications to customers',
  logActivities: 'Log activities and interactions',
  viewPayments: 'View payment records and transaction history',
  allocatePayments: 'Allocate payments to invoices',
  reverseAllocations: 'Reverse previously allocated payments',
  customerAccountsView: 'View customer accounts',
  customerAccountsEdit: 'Edit customer accounts',
  customerAccountsReassign: 'Reassign customer accounts',
  collectionCasesView: 'View collection cases',
  collectionCasesCreate: 'Create collection cases',
  collectionCasesEdit: 'Edit collection cases',
  collectionCasesDelete: 'Delete collection cases',
  collectionCasesReassign: 'Reassign collection cases',
  promiseToPayView: 'View promises to pay',
  promiseToPayCreate: 'Create promises to pay',
  promiseToPayEdit: 'Edit promises to pay',
  promiseToPayDelete: 'Delete promises to pay',
  promiseToPayReassign: 'Reassign promises to pay',
  viewCreditControlDashboard: 'Access to main credit control overview and metrics',
  viewCollectionsDashboard: 'Access to collections queue and team performance metrics',
  viewDisputesDashboard: 'Access to dispute tracking and analytics',
  viewAgeingAnalysis: 'Access to accounts receivable ageing reports',
  viewMultiCompanyDashboard: 'Access to consolidated multi-company view',
  accessManagerViews: 'Can switch between user views and access manager-level functionality',
  adminManageUsersView: 'View user account management',
  adminManageUsersCreate: 'Create new user accounts',
  adminManageUsersEdit: 'Edit existing user accounts',
  adminManageUsersDelete: 'Delete user accounts',
  adminManageCompaniesView: 'View company settings',
  adminManageCompaniesCreate: 'Create new companies',
  adminManageCompaniesEdit: 'Edit company configurations',
  adminManageCompaniesDelete: 'Delete companies',
  adminManageGeneralSettingsView: 'View general system settings',
  adminManageGeneralSettingsCreate: 'Create general settings',
  adminManageGeneralSettingsEdit: 'Edit general settings',
  adminManageGeneralSettingsDelete: 'Delete general settings',
  adminManageIntegrationsView: 'View system integrations',
  adminManageIntegrationsCreate: 'Create new integrations',
  adminManageIntegrationsEdit: 'Edit integration configurations',
  adminManageIntegrationsDelete: 'Delete integrations',
  adminManageTemplatesView: 'View communication templates',
  adminManageTemplatesCreate: 'Create new templates',
  adminManageTemplatesEdit: 'Edit existing templates',
  adminManageTemplatesDelete: 'Delete templates',
  adminManageCollectionStrategiesView: 'View collection strategies',
  adminManageCollectionStrategiesCreate: 'Create new strategies',
  adminManageCollectionStrategiesEdit: 'Edit strategy configurations',
  adminManageCollectionStrategiesDelete: 'Delete strategies',
  adminManageDisputeTypesView: 'View dispute type definitions',
  adminManageDisputeTypesCreate: 'Create new dispute types',
  adminManageDisputeTypesEdit: 'Edit dispute type definitions',
  adminManageDisputeTypesDelete: 'Delete dispute types',
  adminConfigureAIView: 'View AI configuration settings',
  adminConfigureAICreate: 'Create AI configurations',
  adminConfigureAIEdit: 'Edit AI settings',
  adminConfigureAIDelete: 'Delete AI configurations',
};

// Activity Log Constants
const MOCK_ACTIVITY_LOGS: ActivityLog[] = [
  {
    id: '1',
    timestamp: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
    userId: '1',
    userName: 'John Smith',
    userEmail: 'john.smith@company.com',
    action: 'user_login',
    description: 'User logged in successfully',
    ipAddress: '192.168.1.100',
    userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) Chrome/120.0.0.0',
  },
  {
    id: '2',
    timestamp: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
    userId: '2',
    userName: 'Sarah Johnson',
    userEmail: 'sarah.johnson@company.com',
    action: 'user_created',
    description: 'Created new user account for Michael Brown',
    ipAddress: '192.168.1.101',
    userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) Safari/605.1.15',
    resource: 'User',
    resourceId: '5',
  },
  {
    id: '3',
    timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
    userId: '1',
    userName: 'John Smith',
    userEmail: 'john.smith@company.com',
    action: 'settings_updated',
    description: 'Updated company settings',
    ipAddress: '192.168.1.100',
    userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) Chrome/120.0.0.0',
    resource: 'Settings',
    resourceId: 'company',
    changes: {
      companyName: { old: 'Acme Corp', new: 'Acme Corporation' },
      timezone: { old: 'UTC', new: 'America/New_York' },
    },
  },
  {
    id: '4',
    timestamp: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
    userId: '3',
    userName: 'Emily Davis',
    userEmail: 'emily.davis@company.com',
    action: 'role_changed',
    description: 'Changed role for Michael Brown from User to Administrator',
    ipAddress: '192.168.1.102',
    userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) Edge/120.0.0.0',
    resource: 'User',
    resourceId: '5',
    changes: {
      role: { old: 'user', new: 'administrator' },
    },
  },
  {
    id: '5',
    timestamp: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
    userId: '2',
    userName: 'Sarah Johnson',
    userEmail: 'sarah.johnson@company.com',
    action: 'data_exported',
    description: 'Exported user activity log',
    ipAddress: '192.168.1.101',
    userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) Safari/605.1.15',
    resource: 'ActivityLog',
  },
  {
    id: '6',
    timestamp: new Date(Date.now() - 1000 * 60 * 90).toISOString(),
    userId: '1',
    userName: 'John Smith',
    userEmail: 'john.smith@company.com',
    action: 'record_updated',
    description: 'Updated customer record for ABC Industries',
    ipAddress: '192.168.1.100',
    userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) Chrome/120.0.0.0',
    resource: 'Customer',
    resourceId: 'cust-123',
  },
  {
    id: '7',
    timestamp: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
    userId: '4',
    userName: 'Michael Brown',
    userEmail: 'michael.brown@company.com',
    action: 'user_login',
    description: 'User logged in successfully',
    ipAddress: '192.168.1.103',
    userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) Firefox/121.0',
  },
  {
    id: '8',
    timestamp: new Date(Date.now() - 1000 * 60 * 150).toISOString(),
    userId: '2',
    userName: 'Sarah Johnson',
    userEmail: 'sarah.johnson@company.com',
    action: 'permission_changed',
    description: 'Updated permissions for User role',
    ipAddress: '192.168.1.101',
    userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) Safari/605.1.15',
    resource: 'Role',
    resourceId: 'user',
  },
];

const ACTION_LABELS: Record<ActivityType, string> = {
  user_login: 'User Login',
  user_logout: 'User Logout',
  user_created: 'User Created',
  user_updated: 'User Updated',
  user_deleted: 'User Deleted',
  role_changed: 'Role Changed',
  permission_changed: 'Permission Changed',
  settings_updated: 'Settings Updated',
  data_exported: 'Data Exported',
  data_imported: 'Data Imported',
  record_created: 'Record Created',
  record_updated: 'Record Updated',
  record_deleted: 'Record Deleted',
  record_viewed: 'Record Viewed',
};

const ACTION_ICONS: Record<ActivityType, React.ComponentType<{ className?: string }>> = {
  user_login: UserIcon,
  user_logout: UserIcon,
  user_created: Plus,
  user_updated: Edit,
  user_deleted: Trash2,
  role_changed: Shield,
  permission_changed: Shield,
  settings_updated: Shield,
  data_exported: Download,
  data_imported: Download,
  record_created: Plus,
  record_updated: Edit,
  record_deleted: Trash2,
  record_viewed: Eye,
};

const ACTION_COLORS: Record<ActivityType, string> = {
  user_login: 'text-blue-600 bg-blue-50',
  user_logout: 'text-gray-600 bg-gray-50',
  user_created: 'text-green-600 bg-green-50',
  user_updated: 'text-yellow-600 bg-yellow-50',
  user_deleted: 'text-red-600 bg-red-50',
  role_changed: 'text-purple-600 bg-purple-50',
  permission_changed: 'text-purple-600 bg-purple-50',
  settings_updated: 'text-orange-600 bg-orange-50',
  data_exported: 'text-indigo-600 bg-indigo-50',
  data_imported: 'text-indigo-600 bg-indigo-50',
  record_created: 'text-green-600 bg-green-50',
  record_updated: 'text-yellow-600 bg-yellow-50',
  record_deleted: 'text-red-600 bg-red-50',
  record_viewed: 'text-blue-600 bg-blue-50',
};

export function UsersTabsPage() {
  // Hooks
  const navigate = useNavigate();
  
  // Shared data hooks
  const { data: fetchedUsers } = useUsersList();
  const deleteUserMutation = useDeleteUser();
  const updateUserMutation = useUpdateUser();
  const { apiPost } = useApi();

  // User Management State
  const [users, setUsers] = useState<User[]>([]);
  const [filters, setFilters] = useState<UserFilters>({
    search: '',
    role: 'all',
    status: 'all',
    sortBy: 'name_asc',
  });

  // Dialog states for User Management
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [manageIntegrationsDialogOpen, setManageIntegrationsDialogOpen] = useState(false);
  const [manageCompaniesDialogOpen, setManageCompaniesDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Roles & Permissions State
  const [selectedRole, setSelectedRole] = useState<CustomRole | null>(null);
  const [createRoleDialogOpen, setCreateRoleDialogOpen] = useState(false);
  const [deleteRoleDialogOpen, setDeleteRoleDialogOpen] = useState(false);
  const [cannotDeleteDialogOpen, setCannotDeleteDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState<Partial<CustomRole>>({
    name: '',
    description: '',
    permissions: DEFAULT_PERMISSIONS.view_only,
  });

  // Activity Log State
  const [activitySearchQuery, setActivitySearchQuery] = useState('');
  const [selectedActivityUser, setSelectedActivityUser] = useState<string>('all');
  const [selectedAction, setSelectedAction] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [activities] = useState<ActivityLog[]>(MOCK_ACTIVITY_LOGS);

  // Update local users state when fetched data changes
  useEffect(() => {
    if (fetchedUsers) {
      setUsers(fetchedUsers);
    }
  }, [fetchedUsers]);

  // User Management - Filter and sort users
  const filteredUsers = useMemo(() => {
    let filtered = [...users];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.firstName.toLowerCase().includes(searchLower) ||
          user.lastName.toLowerCase().includes(searchLower) ||
          user.email.toLowerCase().includes(searchLower)
      );
    }

    if (filters.role !== 'all') {
      filtered = filtered.filter((user) => user.role === filters.role);
    }

    if (filters.status !== 'all') {
      filtered = filtered.filter((user) => user.status === filters.status);
    }

    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case 'name_asc':
          return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
        case 'name_desc':
          return `${b.firstName} ${b.lastName}`.localeCompare(`${a.firstName} ${a.lastName}`);
        case 'recent':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'role':
          return a.role.localeCompare(b.role);
        default:
          return 0;
      }
    });

    return filtered;
  }, [users, filters]);

  // Roles & Permissions - Calculate user counts
  const filteredRoles = useMemo(() => {
    const roleCounts = users.reduce((acc, user) => {
      acc[user.role] = (acc[user.role] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return SYSTEM_ROLES.map(role => ({
      ...role,
      userCount: roleCounts[role.id] || 0,
    }));
  }, [users]);

  // Activity Log - Filter activities
  const filteredActivities = useMemo(() => {
    let filtered = [...activities];

    if (activitySearchQuery) {
      const query = activitySearchQuery.toLowerCase();
      filtered = filtered.filter(
        (activity) =>
          activity.userName.toLowerCase().includes(query) ||
          activity.userEmail.toLowerCase().includes(query) ||
          activity.description.toLowerCase().includes(query) ||
          activity.ipAddress.includes(query)
      );
    }

    if (selectedActivityUser !== 'all') {
      filtered = filtered.filter((activity) => activity.userId === selectedActivityUser);
    }

    if (selectedAction !== 'all') {
      filtered = filtered.filter((activity) => activity.action === selectedAction);
    }

    if (dateFrom) {
      filtered = filtered.filter(
        (activity) => new Date(activity.timestamp) >= dateFrom
      );
    }
    if (dateTo) {
      const endOfDay = new Date(dateTo);
      endOfDay.setHours(23, 59, 59, 999);
      filtered = filtered.filter(
        (activity) => new Date(activity.timestamp) <= endOfDay
      );
    }

    filtered.sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

    return filtered;
  }, [activities, activitySearchQuery, selectedActivityUser, selectedAction, dateFrom, dateTo]);

  // User Management Handlers
  const handleDeleteUser = (userId: string, reassignToUserId?: string) => {
    deleteUserMutation.mutate(userId, {
      onSuccess: () => {
        setUsers(prevUsers => prevUsers.filter((user) => user.id !== userId));
        if (reassignToUserId) {
          toast.success('User deleted and accounts reassigned successfully');
        } else {
          toast.success('User deleted successfully');
        }
      },
      onError: (error) => {
        toast.error(`Failed to delete user: ${error.message}`);
      },
    });
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setViewDialogOpen(true);
  };

  const handleDeleteUserClick = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleManageIntegrations = (user: User) => {
    setSelectedUser(user);
    setManageIntegrationsDialogOpen(true);
  };

  const handleManageCompanies = (user: User) => {
    setSelectedUser(user);
    setManageCompaniesDialogOpen(true);
  };

  const handleSaveIntegration = async (userId: string, evoUserId: string, externalFirstName: string, externalLastName: string) => {
    try {
      await apiPost(`/api/v1/user/${userId}`, {
        evoUserId: evoUserId || null,
        externalFirstName: externalFirstName || null,
        externalLastName: externalLastName || null,
      });

      setUsers(
        users.map((user) =>
          user.id === userId
            ? { 
                ...user, 
                evoUserId: evoUserId || undefined,
                externalFirstName: externalFirstName || undefined,
                externalLastName: externalLastName || undefined,
                updatedAt: new Date().toISOString() 
              }
            : user
        )
      );
      toast.success('User integration updated successfully');
    } catch (error) {
      console.error('Error saving user integration:', error);
      toast.error('Failed to save user integration');
    }
  };

  const handleUpdateUserRole = async (userId: string, newRole: UserRole) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    try {
      await updateUserMutation.mutateAsync({
        userId,
        data: {
          ...user,
          role: newRole,
          permissions: DEFAULT_PERMISSIONS[newRole],
        },
      });

      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.id === userId
            ? { ...u, role: newRole, permissions: DEFAULT_PERMISSIONS[newRole], updatedAt: new Date().toISOString() }
            : u
        )
      );

      toast.success(`User role updated to ${newRole}`);
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role');
      throw error;
    }
  };

  // Roles & Permissions Handlers
  const handleViewRole = (role: CustomRole) => {
    navigate(`/settings/users/roles/${role.id}`);
  };

  const handleCreateRole = () => {
    if (!newRole.name || !newRole.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    toast.success('Custom roles feature coming soon');
    setCreateRoleDialogOpen(false);
    setNewRole({
      name: '',
      description: '',
      permissions: DEFAULT_PERMISSIONS.view_only,
    });
  };

  const handleDeleteRoleClick = (role: CustomRole) => {
    setSelectedRole(role);
    
    // Check if role is a system role
    if (role.isSystem) {
      toast.error('System roles cannot be deleted');
      return;
    }
    
    // Check if role has users assigned
    if (role.userCount > 0) {
      setCannotDeleteDialogOpen(true);
    } else {
      setDeleteRoleDialogOpen(true);
    }
  };

  const handleDeleteRole = () => {
    if (!selectedRole) return;
    
    if (selectedRole.isSystem) {
      toast.error('Cannot delete system roles');
      return;
    }

    if (selectedRole.userCount > 0) {
      toast.error('Cannot delete role with assigned users');
      return;
    }

    // Create audit log entry before deletion
    console.log(`Audit: Role '${selectedRole.name}' deleted by current user`);
    
    toast.success(`Role '${selectedRole.name}' deleted successfully`);
    setDeleteRoleDialogOpen(false);
    setSelectedRole(null);
  };

  const handleViewUsersForRole = () => {
    if (!selectedRole) return;
    
    // Navigate to Users tab and filter by this role
    setCannotDeleteDialogOpen(false);
    navigate(`/settings/users/roles/${selectedRole.id}`, {
      state: { tab: 'users' }
    });
  };

  const handleDuplicateRole = (role: CustomRole) => {
    // Generate a unique name for the duplicated role
    let duplicateName = `${role.name} (Copy)`;
    let counter = 2;
    
    // Check if a role with this name already exists
    while (filteredRoles.some(r => r.name === duplicateName)) {
      duplicateName = `${role.name} (Copy ${counter})`;
      counter++;
    }

    // Create a new role ID (in real app, this would be generated by backend)
    const newRoleId = `custom_${Date.now()}`;

    // Navigate to the create role page with the duplicated role data
    navigate(`/settings/users/roles-permissions/duplicate/${role.id}`, {
      state: {
        duplicateFrom: role,
        newRoleName: duplicateName,
      }
    });
  };

  // Activity Log Handlers
  const handleExportActivityLog = () => {
    toast.success('Activity log exported successfully');
  };

  const handleClearActivityFilters = () => {
    setActivitySearchQuery('');
    setSelectedActivityUser('all');
    setSelectedAction('all');
    setDateFrom(undefined);
    setDateTo(undefined);
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return format(date, 'MMM d, yyyy HH:mm');
  };

  return (
    <div className="p-6">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold">Users</h1>
          </div>
          <p className="text-muted-foreground mt-2">
            Manage user accounts, roles, permissions, and monitor activity across your organization
          </p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="management" className="space-y-6">
          <TabsList>
            <TabsTrigger value="management">User Management</TabsTrigger>
            <TabsTrigger value="roles">Roles & Permissions</TabsTrigger>
            <TabsTrigger value="activity">User Activity Log</TabsTrigger>
          </TabsList>

          {/* User Management Tab */}
          <TabsContent value="management" className="space-y-6">
            {/* Actions Bar */}
            <div className="flex items-center gap-4">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-9"
                />
              </div>

              {/* Role Filter */}
              <Select
                value={filters.role}
                onValueChange={(value) =>
                  setFilters({ ...filters, role: value as UserRole | 'all' })
                }
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="administrator">Administrator</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="view_only">View Only</SelectItem>
                </SelectContent>
              </Select>

              {/* Status Filter */}
              <Select
                value={filters.status}
                onValueChange={(value) =>
                  setFilters({ ...filters, status: value as UserStatus | 'all' })
                }
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>

              {/* Sort */}
              <Select
                value={filters.sortBy}
                onValueChange={(value) =>
                  setFilters({
                    ...filters,
                    sortBy: value as UserFilters['sortBy'],
                  })
                }
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name_asc">Name A-Z</SelectItem>
                  <SelectItem value="name_desc">Name Z-A</SelectItem>
                  <SelectItem value="recent">Most Recent</SelectItem>
                  <SelectItem value="role">Role</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Results Count */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {filteredUsers.length} of {users.length} users
              </p>
            </div>

            {/* Users Table */}
            <UsersTable
              users={filteredUsers}
              onViewUser={handleViewUser}
              onDeleteUser={handleDeleteUserClick}
              onManageIntegrations={handleManageIntegrations}
              onManageCompanies={handleManageCompanies}
              onUpdateUserRole={handleUpdateUserRole}
              canManageUsers={true}
            />
          </TabsContent>

          {/* Roles & Permissions Tab */}
          <TabsContent value="roles" className="space-y-6">
            {/* Actions Bar */}
            <div className="flex items-center justify-end gap-4">
              <Button onClick={() => navigate('/settings/users/roles-permissions/new')} className="bg-teal-600 hover:bg-teal-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Role
              </Button>
            </div>

            {/* Roles Table */}
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Role Name</TableHead>
                      <TableHead>Role Type</TableHead>
                      <TableHead className="text-center">Users Assigned</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Modified</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRoles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-12">
                          <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                          <h3 className="text-lg font-semibold mb-2">No roles found</h3>
                          <p className="text-muted-foreground">
                            Create a new role to get started
                          </p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRoles.map((role) => (
                        <TableRow key={role.id} className="hover:bg-muted/50 cursor-pointer">
                          <TableCell 
                            className="font-bold"
                            onClick={() => handleViewRole(role)}
                          >
                            {role.name}
                          </TableCell>
                          <TableCell>
                            {role.isSystem ? (
                              <Badge variant="secondary" className="bg-gray-100 text-gray-700 hover:bg-gray-100">
                                Platform Role
                              </Badge>
                            ) : (
                              <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                                Custom Role
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {role.userCount}
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                              Active
                            </Badge>
                          </TableCell>
                          <TableCell>
                            05 Feb 2026
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDuplicateRole(role);
                                }}
                                title="Duplicate role"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              {!role.isSystem && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteRoleClick(role);
                                  }}
                                  title="Delete role"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewRole(role);
                                }}
                                title="View/Edit role"
                              >
                                <ChevronRight className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Activity Log Tab */}
          <TabsContent value="activity" className="space-y-6">
            {/* Filters */}
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    {/* Search */}
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by user, description, or IP address..."
                        value={activitySearchQuery}
                        onChange={(e) => setActivitySearchQuery(e.target.value)}
                        className="pl-9"
                      />
                    </div>

                    {/* User Filter */}
                    <Select value={selectedActivityUser} onValueChange={setSelectedActivityUser}>
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="All Users" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Users</SelectItem>
                        {users.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.firstName} {user.lastName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* Action Filter */}
                    <Select value={selectedAction} onValueChange={setSelectedAction}>
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="All Actions" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Actions</SelectItem>
                        {Object.entries(ACTION_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Date From */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-[200px] justify-start text-left font-normal',
                            !dateFrom && 'text-muted-foreground'
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {dateFrom ? format(dateFrom, 'MMM d, yyyy') : 'From date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={dateFrom}
                          onSelect={setDateFrom}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>

                    {/* Date To */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-[200px] justify-start text-left font-normal',
                            !dateTo && 'text-muted-foreground'
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {dateTo ? format(dateTo, 'MMM d, yyyy') : 'To date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={dateTo}
                          onSelect={setDateTo}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>

                    <Button variant="outline" onClick={handleClearActivityFilters}>
                      <Filter className="w-4 h-4 mr-2" />
                      Clear Filters
                    </Button>

                    <div className="flex-1" />

                    <Button onClick={handleExportActivityLog}>
                      <Download className="w-4 h-4 mr-2" />
                      Export
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Results Count */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {filteredActivities.length} of {activities.length} activities
              </p>
            </div>

            {/* Activity Log Table */}
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[180px]">Timestamp</TableHead>
                      <TableHead className="w-[140px]">Action</TableHead>
                      <TableHead className="w-[200px]">User</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-[140px]">IP Address</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredActivities.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-12">
                          <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                          <h3 className="text-lg font-semibold mb-2">No activities found</h3>
                          <p className="text-muted-foreground">
                            Try adjusting your filters to see more results
                          </p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredActivities.map((activity) => {
                        const Icon = ACTION_ICONS[activity.action];
                        return (
                          <TableRow key={activity.id}>
                            <TableCell className="font-mono text-sm">
                              {formatTimestamp(activity.timestamp)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className={cn('p-1.5 rounded', ACTION_COLORS[activity.action])}>
                                  <Icon className="w-3.5 h-3.5" />
                                </div>
                                <span className="text-sm font-medium">
                                  {ACTION_LABELS[activity.action]}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-0.5">
                                <p className="text-sm font-medium">{activity.userName}</p>
                                <p className="text-xs text-muted-foreground">{activity.userEmail}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <p className="text-sm">{activity.description}</p>
                                {activity.resource && (
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-xs">
                                      {activity.resource}
                                      {activity.resourceId && `: ${activity.resourceId}`}
                                    </Badge>
                                  </div>
                                )}
                                {activity.changes && (
                                  <div className="text-xs text-muted-foreground mt-1">
                                    {Object.entries(activity.changes).map(([key, value]) => (
                                      <div key={key}>
                                        {key}: <span className="line-through">{value.old}</span> →{' '}
                                        <span className="font-medium">{value.new}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {activity.ipAddress}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* User Management Dialogs */}
        <ViewUserDialog
          open={viewDialogOpen}
          onOpenChange={setViewDialogOpen}
          user={selectedUser}
        />

        <DeleteUserDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          user={selectedUser}
          users={users}
          onDeleteUser={handleDeleteUser}
        />

        <ManageUserIntegrationsDialog
          open={manageIntegrationsDialogOpen}
          onOpenChange={setManageIntegrationsDialogOpen}
          user={selectedUser}
          onSave={handleSaveIntegration}
        />

        <ManageUserCompaniesDialog
          open={manageCompaniesDialogOpen}
          onOpenChange={setManageCompaniesDialogOpen}
          user={selectedUser}
        />

        {/* Roles & Permissions Dialogs */}
        <Dialog open={createRoleDialogOpen} onOpenChange={setCreateRoleDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedRole ? 'Edit Role' : 'Create New Role'}
              </DialogTitle>
              <DialogDescription>
                Define a custom role with specific permissions
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="role-name">Role Name *</Label>
                <Input
                  id="role-name"
                  placeholder="e.g., Collection Manager"
                  value={newRole.name}
                  onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role-description">Description *</Label>
                <Input
                  id="role-description"
                  placeholder="Describe the role's purpose and responsibilities"
                  value={newRole.description}
                  onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                />
              </div>

              <div className="space-y-4">
                <Label>Permissions</Label>
                <div className="space-y-3 border rounded-lg p-4">
                  {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                    <div key={key} className="flex items-start justify-between gap-4">
                      <div className="space-y-0.5">
                        <Label htmlFor={`perm-${key}`} className="font-medium">
                          {label}
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          {PERMISSION_DESCRIPTIONS[key as keyof UserPermissions]}
                        </p>
                      </div>
                      <Switch
                        id={`perm-${key}`}
                        checked={typeof newRole.permissions?.[key as keyof UserPermissions] === 'boolean' ? newRole.permissions?.[key as keyof UserPermissions] as boolean : false}
                        onCheckedChange={(checked) =>
                          setNewRole({
                            ...newRole,
                            permissions: {
                              ...newRole.permissions!,
                              [key]: checked,
                            },
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateRoleDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateRole}>
                {selectedRole ? 'Save Changes' : 'Create Role'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Role Confirmation Dialog - For roles with NO users */}
        <Dialog open={deleteRoleDialogOpen} onOpenChange={setDeleteRoleDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Role</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete '{selectedRole?.name}'? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteRoleDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteRole}>
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cannot Delete Dialog - For roles WITH users assigned */}
        <Dialog open={cannotDeleteDialogOpen} onOpenChange={setCannotDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                </div>
                <DialogTitle>Cannot Delete Role</DialogTitle>
              </div>
              <DialogDescription className="space-y-3">
                <p>
                  This role cannot be deleted because it has {selectedRole?.userCount} {selectedRole?.userCount === 1 ? 'user' : 'users'} assigned.
                </p>
                <p>
                  Please reassign these users to another role before deleting.
                </p>
                {selectedRole && selectedRole.userCount > 0 && (
                  <div className="mt-4 rounded-lg bg-muted p-3">
                    <p className="text-sm font-medium mb-2">Assigned Users:</p>
                    <div className="text-sm text-muted-foreground">
                      {users
                        .filter(user => user.role === selectedRole.id)
                        .slice(0, 5)
                        .map(user => (
                          <div key={user.id} className="py-1">
                            • {user.firstName} {user.lastName} ({user.email})
                          </div>
                        ))}
                      {selectedRole.userCount > 5 && (
                        <div className="py-1 font-medium">
                          and {selectedRole.userCount - 5} more...
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setCannotDeleteDialogOpen(false)}>
                Close
              </Button>
              <Button onClick={handleViewUsersForRole}>
                View Users
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
